import { EventObject } from './events'
import { NotificationBase, NOTIFICATION_STATUS_ERROR, NOTIFICATION_STATUS_INFO } from '../types/Notification';

export const MULTIPLE_ANALYSIS_EVENT_TYPE = "multiple-analysis"

/**
 * Factory function type for creating notifications from events
 */
export type EventNotificationFactory = (event: EventObject) => NotificationBase | null

/**
 * Map of event types to notification factories
 * Add new entries here to customize notifications for specific event types
 */
export const eventNotificationFactories: Map<string, EventNotificationFactory> = new Map([
  [
    MULTIPLE_ANALYSIS_EVENT_TYPE,
    (event: EventObject): NotificationBase => ({
      status: NOTIFICATION_STATUS_ERROR,
      message: `Sheila a été dans l'impossibilité d'identifier la demande à l'origine des résultats retrouvés dans ${event?.data?.source}. Voir les notifications pour plus d'informations.`,
      detail: `ID: ${event.id}`
    }),
  ],
])

/**
 * Get the notification factory for a given event type
 * Falls back to the default factory if no specific factory is registered
 */
export function getNotificationFactory(eventType: string): EventNotificationFactory | undefined {
  return eventNotificationFactories.get(eventType)
}

/**
 * Create a notification from an event using the registered factory
 * Returns null if the factory returns null (indicating no notification should be shown)
 */
export function createNotificationFromEvent(event: EventObject): NotificationBase | null {
  const factory = getNotificationFactory(event.type)
  return factory ? factory(event) : null
}

/**
 * Register a custom notification factory for an event type
 * Use this to override default behavior or add support for new event types
 */
export function registerEventNotificationFactory(eventType: string, factory: EventNotificationFactory): void {
  eventNotificationFactories.set(eventType, factory)
}

// Ticket #822 - Un fichier résultat issu d'une feuille de travail antérieure au
// ticket ne porte pas les compteurs séquentiels : quand plusieurs demandes
// correspondent au même numéro de prélèvement, Sheila ne peut pas trancher et
// laisse le résultat de côté sans bloquer les autres.
//
// Ce n'est pas une erreur mais une limite connue de l'historique. Le ticket
// demande, plutôt qu'une alerte par résultat, « un message d'information :
// "X prélèvements non importés" et si possible le numéro du prélèvement
// concerné ». D'où le regroupement ci-dessous.
export function isLegacyAmbiguityEvent(event: EventObject): boolean {
  return event.type === MULTIPLE_ANALYSIS_EVENT_TYPE && event?.data?.legacyAmbiguity === true
}

/**
 * Build the aggregated notification for a batch of legacy ambiguity events.
 * Returns null when the batch holds none.
 *
 * `notifiedSampleIds` porte les numéros de prélèvement déjà annoncés par les
 * lots de polling précédents : les événements d'un même import peuvent tomber
 * sur deux cycles (limite de la requête, pipeline plus lent que l'intervalle),
 * et sans cette mémoire le même prélèvement serait annoncé deux fois. Le set
 * est enrichi en place avec les numéros nouvellement annoncés.
 */
export function createLegacyAmbiguityNotification(events: EventObject[], notifiedSampleIds?: Set<string>): NotificationBase | null {
  const ambiguous = events.filter(isLegacyAmbiguityEvent)
  if (ambiguous.length === 0) return null

  // Un même prélèvement peut produire plusieurs événements (une classe, un
  // catalogue) : c'est bien un nombre de prélèvements qui est annoncé.
  const batchSampleIds = Array.from(new Set(
    ambiguous
      .map(event => event?.data?.sampleIndex)
      .filter((sampleIndex): sampleIndex is string => typeof sampleIndex === 'string' && sampleIndex !== '')
  ))

  const sampleIds = batchSampleIds.filter(sampleId => !notifiedSampleIds?.has(sampleId))

  // Tous les prélèvements du lot ont déjà été annoncés : rien de neuf à dire.
  if (batchSampleIds.length > 0 && sampleIds.length === 0) return null

  sampleIds.forEach(sampleId => notifiedSampleIds?.add(sampleId))

  // Fallback historique quand aucun événement ne porte de numéro de
  // prélèvement : on compte les événements (déjà dédoublonnés par id en
  // amont), faute de mieux.
  const count = sampleIds.length > 0 ? sampleIds.length : ambiguous.length

  return {
    status: NOTIFICATION_STATUS_INFO,
    message: `${count} prélèvement${count > 1 ? 's' : ''} non importé${count > 1 ? 's' : ''}`,
    detail: sampleIds.length > 0
      ? `N° de prélèvement : ${sampleIds.join(', ')}`
      : undefined,
  }
}

/**
 * Turn a batch of freshly received events into the notifications to display,
 * aggregating the ones that must not be reported one by one.
 */
export function createNotificationsFromEvents(events: EventObject[], notifiedSampleIds?: Set<string>): NotificationBase[] {
  const notifications: NotificationBase[] = []

  for (const event of events) {
    if (isLegacyAmbiguityEvent(event)) continue

    const notification = createNotificationFromEvent(event)
    if (notification) notifications.push(notification)
  }

  const aggregated = createLegacyAmbiguityNotification(events, notifiedSampleIds)
  if (aggregated) notifications.push(aggregated)

  return notifications
}